import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  ChangeActiveViewPayload,
  Dropin,
  PaymentMethodPayload
} from 'braintree-web-drop-in';

import { isNil, omitBy } from 'lodash';
import { AnalyticsContext } from '../../../../ModularReduxAnalytics';
import { useApp } from '../../../../state/app';

import Button from '../../../../components/UI/Button';
import ConfirmAddress from '../../ConfirmAddress';
import BraintreeDropin from './BraintreeDropin';

import {
  BaseFundingAccountProps,
  BillingAddressProps,
  convertApplePayBillingAddressToAddress,
  convertGooglePayBillingAddressToAddress
} from '../shared';

import css from '../FundingAccount.module.scss';
import { useToggles } from '../../../../state/toggles';

export interface DebitProps
  extends Omit<
    BaseFundingAccountProps,
    'getInitialFundingAmount' | 'submitPaymentMethod'
  > {
  loadAmount: number;
  enhancedDebitOptions: {
    applePayEnabled: boolean;
    googlePayEnabled: boolean;
    threeDSEnabled: boolean;
    venmoEnabled: boolean;
    paypalEnabled: boolean;
  };
  handlePayment: (
    nonce: string,
    type: string,
    billingAddress?: BillingAddressProps,
    details?: { [key: string]: any }
  ) => void;
  createErrorDispatch: (msg: string) => void;
  clearErrorDispatch: () => void;
}

export default function Debit({
  fundingSource,
  setFundingSource,
  loadAmount,
  enhancedDebitOptions,
  handlePayment,
  clearErrorDispatch,
  createErrorDispatch
}: DebitProps) {
  const { analytics } = useContext(AnalyticsContext);
  const { loading, answers, braintreeAccessToken } = useApp();
  const { challengeRequested3dsEnabled } = useToggles();

  const [isDebitOpen, setDebitOpen] = useState(false);
  const [isModalOpen, showAddressModal] = useState(false);
  const [shouldShowAddress, setShouldShowAddress] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const [dropinInstance, setDropinInstance] = useState<Dropin | undefined>(
    undefined
  );

  useEffect(() => {
    if (!loading) {
      showAddressModal(false);
    }
  }, [loading]);

  useEffect(() => {
    if (answers) {
      const { setBillingAddress, setLegalAddress } = answers;
      setAddress(setBillingAddress || setLegalAddress);
    }
  }, [answers?.setBillingAddress, answers?.setLegalAddress]);

  // This effect manages the billing address widget lifecycle. Anytime the manual debit card entry
  // is selected, we should show the billing address widget - otherwise, hide it.
  useEffect(() => {
    if (dropinInstance) {
      // getAvailablePaymentOptions() is at dropin.js, line 614, but TS definition doesn't have it for some reason, so we need to override
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const paymentOptions = dropinInstance.getAvailablePaymentOptions();
      // Let's first set initial state. If only card is available, then go ahead and
      // open the billing address widget by default.
      if (paymentOptions && paymentOptions.length > 1) {
        setShouldShowAddress(false);
      } else {
        setShouldShowAddress(true);
      }

      analytics.trackEvent('Debit Instruments Displayed', {
        eventProps: {
          apple_pay: paymentOptions && paymentOptions.includes('applePay'),
          google_pay: paymentOptions && paymentOptions.includes('googlePay')
        }
      });

      // Attach a view listener as well to show or hide the address widget accordingly and send segment event
      dropinInstance.on(
        'changeActiveView',
        (changeActiveView: ChangeActiveViewPayload) => {
          if (changeActiveView.newViewId === 'card') {
            setShouldShowAddress(true);
          } else {
            setShouldShowAddress(false);
          }
          if (
            ['card', 'applePay', 'googlePay', 'paypal', 'venmo'].includes(
              changeActiveView.newViewId
            )
          ) {
            analytics.trackEvent('Debit Instrument Selected', {
              eventProps: {
                funding_instrument: changeActiveView.newViewId
              }
            });
          }
        }
      );
      // https://braintree.github.io/braintree-web/current/ThreeDSecure.html#event:authentication-modal-render
      // TS Definition is missing this event so we need to override
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dropinInstance.on('3ds:authentication-modal-render', () => {
        analytics.page('Registration', '3DS Verification');
      });
    }
  }, [dropinInstance]);

  const selectDebit = () => {
    if (fundingSource === 'debit') {
      setDebitOpen(false);
      setFundingSource(null);
    } else {
      setDebitOpen(true);
      setFundingSource('debit');
      const eventProps = { during_registration: true };
      analytics.trackEvent('Add Debit Card Clicked', { eventProps });
      analytics.trackEvent('Save Debit Viewed', { eventProps });
    }
  };

  const saveDebitAccount = async () => {
    clearErrorDispatch();
    if (
      dropinInstance &&
      typeof dropinInstance.requestPaymentMethod === 'function'
    ) {
      const threeDSecureParameters = {
        amount: `${loadAmount}`,
        email: answers?.setUserEmail,
        mobilePhoneNumber: answers?.setPhoneNumber?.slice(1), // remove leading "1"
        billingAddress: omitBy(
          {
            givenName: answers?.setLegalFirstName,
            surname: answers?.setLegalLastName,
            streetAddress: address.address,
            locality: address.city,
            region: address.state,
            postalCode: address.zip,
            countryCodeAlpha2: 'US'
          },
          isNil
        ),
        challengeRequested: challengeRequested3dsEnabled.enabled
      };

      try {
        const paymentMethodPayload: PaymentMethodPayload =
          await dropinInstance.requestPaymentMethod({
            threeDSecure: threeDSecureParameters
          });

        // Type for Debit Cards is 'CreditCard'
        if (paymentMethodPayload.type === 'CreditCard') {
          handlePayment(
            paymentMethodPayload.nonce,
            paymentMethodPayload.type,
            address,
            {
              deviceData: paymentMethodPayload.deviceData
            }
          );
        } else if (paymentMethodPayload.type === 'ApplePayCard') {
          handlePayment(
            paymentMethodPayload.nonce,
            paymentMethodPayload.type,
            convertApplePayBillingAddressToAddress(
              paymentMethodPayload.details.rawPaymentData.billingContact
            ),
            {
              deviceData: paymentMethodPayload.deviceData
            }
          );
        } else if (paymentMethodPayload.type === 'AndroidPayCard') {
          handlePayment(
            paymentMethodPayload.nonce,
            paymentMethodPayload.type,
            convertGooglePayBillingAddressToAddress(
              paymentMethodPayload.details.rawPaymentData.paymentMethodData.info
                ?.billingAddress
            ),
            {
              deviceData: paymentMethodPayload.deviceData
            }
          );
        } else if (paymentMethodPayload.type === 'PayPalAccount') {
          handlePayment(
            paymentMethodPayload.nonce,
            paymentMethodPayload.type,
            undefined,
            {
              ...paymentMethodPayload.details,
              deviceData: paymentMethodPayload.deviceData
            }
          );
        } else if (paymentMethodPayload.type === 'VenmoAccount') {
          handlePayment(
            paymentMethodPayload.nonce,
            paymentMethodPayload.type,
            undefined,
            {
              ...paymentMethodPayload.details,
              deviceData: paymentMethodPayload.deviceData
            }
          );
        }
      } catch (err: any) {
        analytics.trackEvent('Registration Error Triggered', {
          eventProps: {
            registration_step: 'debit',
            error_message: err ? err.message : ''
          }
        });
        createErrorDispatch('Please check your information and try again');
      }
    }
  };

  return (
    <div
      className={`${!isDebitOpen ? css.AddFundingAccountUnselected : ''} 
      ${css.AddFundingAccountSecondary} ${css.AddFundingAccountContainer}
      `}
      data-e2e="manual-debit"
    >
      <div className={css.ManualAchContainer}>
        {isModalOpen && (
          <ConfirmAddress
            onClose={() => showAddressModal(false)}
            onCancel={() => showAddressModal(false)}
          />
        )}
        <div
          className={css.ManualAchWrapper}
          onClick={selectDebit}
          data-testid="add-manual-debit"
          data-e2e="add-manual-debit"
        >
          <div className={css.AddFundingAccountItemText}>
            <p className={css.AddFundingAccountItemHeader}>
              {fundingSource !== 'debit'
                ? 'Add a debit card'
                : 'Add your personal debit card'}
            </p>
            <p className={css.AddFundingAccountItemSubheader}>
              Not eligible for cash back or Savings Rewards*
            </p>
            {fundingSource !== 'debit' && (
              <div className={css.debitOptions}>
                {enhancedDebitOptions.applePayEnabled && (
                  <img src="../../static/apple-pay-logo.svg" alt="Apple Pay" />
                )}
                {enhancedDebitOptions.googlePayEnabled && (
                  <img
                    src="../../static/google-pay-logo.svg"
                    alt="Google Pay"
                  />
                )}
                <img src="../../static/debit-card-logo.svg" alt="Debit Card" />
              </div>
            )}
          </div>
        </div>

        <div
          className={
            fundingSource !== 'debit' && css.hideForm ? css.hideForm : ''
          }
          data-e2e="debit-form-container"
          style={{ textAlign: 'left' }}
        >
          <BraintreeDropin
            braintreeAccessToken={braintreeAccessToken as string}
            onSetInstance={setDropinInstance}
            loadAmount={loadAmount}
            enhancedDebitOptions={enhancedDebitOptions}
          />

          {address && shouldShowAddress ? (
            <>
              <br />
              <p className={css.AddFundingAccountItemHeader}>
                Debit card billing address
              </p>
              <div
                data-e2e="debit-billing-address"
                data-testid="debit-billing-address"
                className="fs-mask"
              >
                <p>
                  {address.address || address.streetAddress}
                  <br />
                  {address.address2 && `${address.address2}\n`}
                  {address.streetAddress2 && `${address.streetAddress2}\n`}
                  {address.city}, {address.state}{' '}
                  {address.zip || address.postalCode}
                </p>
                <a
                  onClick={() => showAddressModal(true)}
                  data-e2e="different-address"
                >
                  Use a different address
                </a>
              </div>
            </>
          ) : shouldShowAddress ? (
            <div style={{ padding: '10px 50px 0' }}>
              <a
                onClick={() => showAddressModal(true)}
                data-e2e="different-address"
                style={{ fontWeight: 'bold' }}
              >
                Add billing address
              </a>
            </div>
          ) : (
            <div />
          )}
          <Button
            text="Save Debit Card"
            loading={loading}
            onClick={saveDebitAccount}
          />
        </div>
      </div>
    </div>
  );
}
